import React, { useState, useEffect } from 'react';
import { 
  Search, Package, MapPin, Calendar, Clock, User, Truck, 
  CheckCircle, AlertCircle, XCircle, FileText, Phone, Mail,
  Navigation, Info, ArrowRight, Download, Printer
} from 'lucide-react';
import '../styles/ReportsForm.css';
import '../styles/ConsignmentTracking.css';
import apiClient from '../services/api';

const ConsignmentTracking = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);

  // Session
  const session = getSessionData();

  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }

  useEffect(() => {
    loadRecentSearches();
  }, []);

  const loadRecentSearches = () => {
    try {
      const searches = JSON.parse(localStorage.getItem('recent_tracking_searches') || '[]');
      setRecentSearches(searches.slice(0, 5));
    } catch (err) {
      console.error('Error loading recent searches:', err);
    }
  };

  const saveRecentSearch = (consignmentNo) => {
    try {
      let searches = JSON.parse(localStorage.getItem('recent_tracking_searches') || '[]');
      searches = [consignmentNo, ...searches.filter(s => s !== consignmentNo)].slice(0, 10);
      localStorage.setItem('recent_tracking_searches', JSON.stringify(searches));
      setRecentSearches(searches.slice(0, 5));
    } catch (err) {
      console.error('Error saving recent search:', err);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchQuery.trim()) {
      setError('Please enter a consignment number');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);

    try {
      const response = await apiClient.get('/ConsignmentTracking.php', {
        params: { consignmentNo: searchQuery.trim() }
      });

      if (response.data.success) {
        setTrackingData(response.data.data);
        saveRecentSearch(searchQuery.trim());
      } else {
        setError(response.data.message || 'Consignment not found');
      }
    } catch (err) {
      console.error('Error tracking consignment:', err);
      setError('Error tracking consignment: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSearch = (consignmentNo) => {
    setSearchQuery(consignmentNo);
    setTimeout(() => {
      handleSearch();
    }, 100);
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="status-icon success" size={24} />;
      case 'in transit':
      case 'dispatched':
        return <Truck className="status-icon warning" size={24} />;
      case 'pending':
      case 'received':
        return <Package className="status-icon info" size={24} />;
      case 'cancelled':
      case 'returned':
        return <XCircle className="status-icon danger" size={24} />;
      default:
        return <AlertCircle className="status-icon" size={24} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'green';
      case 'in transit':
      case 'dispatched':
        return 'blue';
      case 'pending':
      case 'received':
        return 'yellow';
      case 'cancelled':
      case 'returned':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <div className="report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h2>Consignment Tracking</h2>
          <p className="subtitle">Real-time Shipment Status & Location</p>
        </div>
      </div>

      {/* Search Section */}
      <div className="tracking-search-section no-print">
        <form onSubmit={handleSearch} className="tracking-search-form">
          <div className="search-input-wrapper">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Enter Consignment Number (e.g., LT-000123)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tracking-search-input"
            />
            <button 
              type="submit" 
              disabled={loading}
              className="btn-track"
            >
              {loading ? 'Tracking...' : 'Track'}
            </button>
          </div>
        </form>

        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <div className="recent-searches">
            <span className="recent-label">Recent:</span>
            {recentSearches.map((search, index) => (
              <button
                key={index}
                onClick={() => handleQuickSearch(search)}
                className="recent-chip"
              >
                {search}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="report-alert alert-error">
          <div className="alert-icon">⚠</div>
          <div>{error}</div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="report-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Tracking your consignment...</p>
        </div>
      )}

      {/* Tracking Results */}
      {trackingData && !loading && (
        <div className="tracking-results">
          {/* Print Button */}
          <div className="report-toolbar no-print" style={{ justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button onClick={handlePrint} className="btn-print">
              <Printer size={18} />
              Print
            </button>
          </div>

          {/* Status Overview Card */}
          <div className="status-overview-card">
            <div className="status-header">
              <div className="status-icon-wrapper">
                {getStatusIcon(trackingData.consignment?.StatusName)}
              </div>
              <div className="status-info">
                <h2 className="consignment-number">
                  {trackingData.consignment?.ConsignmentNo}
                </h2>
                <div className="status-badge-large">
                  <span className={`status-badge ${getStatusColor(trackingData.consignment?.StatusName)}`}>
                    {trackingData.consignment?.StatusName || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="quick-stats">
              <div className="stat-item">
                <Calendar size={18} />
                <div>
                  <div className="stat-label">Booking Date</div>
                  <div className="stat-value">
                    {new Date(trackingData.consignment?.ConsignmentDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="stat-item">
                <Package size={18} />
                <div>
                  <div className="stat-label">Packages</div>
                  <div className="stat-value">
                    {trackingData.consignment?.NoOfPkgs || 0} pcs
                  </div>
                </div>
              </div>

              <div className="stat-item">
                <Truck size={18} />
                <div>
                  <div className="stat-label">Weight</div>
                  <div className="stat-value">
                    {trackingData.consignment?.Weight || 0} kg
                  </div>
                </div>
              </div>

              {trackingData.consignment?.DeliveryDate && (
                <div className="stat-item">
                  <CheckCircle size={18} />
                  <div>
                    <div className="stat-label">Delivered</div>
                    <div className="stat-value">
                      {new Date(trackingData.consignment?.DeliveryDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          {trackingData.timeline && trackingData.timeline.length > 0 && (
            <div className="tracking-timeline-section">
              <h3 className="section-title">
                <Navigation size={18} />
                Tracking Timeline
              </h3>
              <div className="tracking-timeline">
                {trackingData.timeline.map((event, index) => (
                  <div key={index} className={`timeline-item ${event.IsCompleted ? 'completed' : 'pending'}`}>
                    <div className="timeline-marker">
                      {event.IsCompleted ? (
                        <CheckCircle size={20} />
                      ) : (
                        <div className="timeline-dot"></div>
                      )}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <h4 className="timeline-title">{event.EventName}</h4>
                        {event.EventDate && (
                          <span className="timeline-date">
                            <Calendar size={14} />
                            {new Date(event.EventDate).toLocaleDateString()}
                            {event.EventTime && ` ${event.EventTime}`}
                          </span>
                        )}
                      </div>
                      {event.Location && (
                        <p className="timeline-location">
                          <MapPin size={14} />
                          {event.Location}
                        </p>
                      )}
                      {event.Description && (
                        <p className="timeline-description">{event.Description}</p>
                      )}
                      {event.PersonName && (
                        <p className="timeline-person">
                          <User size={14} />
                          {event.PersonName}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shipment Details */}
          <div className="shipment-details-grid">
            {/* Shipper Details */}
            <div className="detail-card">
              <h3 className="detail-card-title">
                <User size={18} />
                Shipper Details
              </h3>
              <div className="detail-content">
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{trackingData.consignment?.ShipperName || 'N/A'}</span>
                </div>
                {trackingData.consignment?.ShipperPhone && (
                  <div className="detail-row">
                    <Phone size={14} />
                    <span className="detail-value">{trackingData.consignment.ShipperPhone}</span>
                  </div>
                )}
                {trackingData.consignment?.ShipperMobile && (
                  <div className="detail-row">
                    <Phone size={14} />
                    <span className="detail-value">{trackingData.consignment.ShipperMobile}</span>
                  </div>
                )}
                <div className="detail-row">
                  <MapPin size={14} />
                  <span className="detail-value">
                    {[
                      trackingData.consignment?.ShipperCity,
                      trackingData.consignment?.ShipperCountry
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </span>
                </div>
                {trackingData.consignment?.ShipperLocation && (
                  <div className="detail-row">
                    <span className="detail-value">{trackingData.consignment.ShipperLocation}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Consignee Details */}
            <div className="detail-card">
              <h3 className="detail-card-title">
                <User size={18} />
                Consignee Details
              </h3>
              <div className="detail-content">
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{trackingData.consignment?.ConsigneeName || 'N/A'}</span>
                </div>
                {trackingData.consignment?.ConsigneePhone && (
                  <div className="detail-row">
                    <Phone size={14} />
                    <span className="detail-value">{trackingData.consignment.ConsigneePhone}</span>
                  </div>
                )}
                {trackingData.consignment?.ConsigneeMobile && (
                  <div className="detail-row">
                    <Phone size={14} />
                    <span className="detail-value">{trackingData.consignment.ConsigneeMobile}</span>
                  </div>
                )}
                <div className="detail-row">
                  <MapPin size={14} />
                  <span className="detail-value">
                    {[
                      trackingData.consignment?.ConsigneeCity,
                      trackingData.consignment?.ConsigneeCountry
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </span>
                </div>
                {trackingData.consignment?.ConsigneeLocation && (
                  <div className="detail-row">
                    <span className="detail-value">{trackingData.consignment.ConsigneeLocation}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Shipment Info */}
            <div className="detail-card">
              <h3 className="detail-card-title">
                <Package size={18} />
                Shipment Information
              </h3>
              <div className="detail-content">
                <div className="detail-row">
                  <span className="detail-label">Description:</span>
                  <span className="detail-value">{trackingData.consignment?.Description || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Pieces:</span>
                  <span className="detail-value">{trackingData.consignment?.Pieces || 0}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Packages:</span>
                  <span className="detail-value">{trackingData.consignment?.NoOfPkgs || 0}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Weight:</span>
                  <span className="detail-value">{trackingData.consignment?.Weight || 0} kg</span>
                </div>
                {trackingData.consignment?.Volume > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Volume:</span>
                    <span className="detail-value">{trackingData.consignment.Volume} cbm</span>
                  </div>
                )}
              </div>
            </div>

            {/* Route Info */}
            <div className="detail-card">
              <h3 className="detail-card-title">
                <Navigation size={18} />
                Route Information
              </h3>
              <div className="detail-content">
                {trackingData.consignment?.OriginName && (
                  <div className="detail-row">
                    <span className="detail-label">Origin:</span>
                    <span className="detail-value">{trackingData.consignment.OriginName}</span>
                  </div>
                )}
                {trackingData.consignment?.DestinationName && (
                  <div className="detail-row">
                    <span className="detail-label">Destination:</span>
                    <span className="detail-value">{trackingData.consignment.DestinationName}</span>
                  </div>
                )}
                {trackingData.consignment?.VehicleNo && (
                  <div className="detail-row">
                    <Truck size={14} />
                    <span className="detail-value">Vehicle: {trackingData.consignment.VehicleNo}</span>
                  </div>
                )}
                {trackingData.consignment?.DriverName && (
                  <div className="detail-row">
                    <User size={14} />
                    <span className="detail-value">Driver: {trackingData.consignment.DriverName}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          {trackingData.consignment?.DeliveryDate && (
            <div className="delivery-info-card">
              <h3 className="section-title">
                <CheckCircle size={18} />
                Delivery Information
              </h3>
              <div className="delivery-grid">
                <div className="delivery-item">
                  <span className="delivery-label">Delivered On:</span>
                  <span className="delivery-value">
                    {new Date(trackingData.consignment.DeliveryDate).toLocaleString()}
                  </span>
                </div>
                {trackingData.consignment?.ReceivedBy && (
                  <div className="delivery-item">
                    <span className="delivery-label">Received By:</span>
                    <span className="delivery-value">{trackingData.consignment.ReceivedBy}</span>
                  </div>
                )}
                {trackingData.consignment?.DeliveredBy && (
                  <div className="delivery-item">
                    <span className="delivery-label">Delivered By:</span>
                    <span className="delivery-value">{trackingData.consignment.DeliveredBy}</span>
                  </div>
                )}
              </div>
              {trackingData.consignment?.PODRemarks && (
                <div className="delivery-remarks">
                  <Info size={16} />
                  <span>{trackingData.consignment.PODRemarks}</span>
                </div>
              )}
            </div>
          )}

          {/* Remarks */}
          {trackingData.consignment?.Remarks && (
            <div className="remarks-card">
              <h3 className="section-title">
                <FileText size={18} />
                Remarks
              </h3>
              <p className="remarks-text">{trackingData.consignment.Remarks}</p>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!trackingData && !loading && !error && (
        <div className="tracking-empty-state">
          <div className="empty-icon-large">
            <Package size={80} />
          </div>
          <h3 className="empty-title">Track Your Consignment</h3>
          <p className="empty-message">
            Enter your consignment number above to track your shipment in real-time
          </p>
          <div className="empty-features">
            <div className="feature-item">
              <CheckCircle size={20} />
              <span>Real-time Updates</span>
            </div>
            <div className="feature-item">
              <MapPin size={20} />
              <span>Location Tracking</span>
            </div>
            <div className="feature-item">
              <Clock size={20} />
              <span>Delivery Timeline</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsignmentTracking;